import { datadogLogs } from '@datadog/browser-logs';
import type { ConfigService } from '@packages/core/config';
import { Constants } from '@packages/core/config';
import type { LogDestination, LogEntry } from '@packages/core/logging';
import type { LogLevel } from '@packages/core/logging';
import { LOG_LEVEL_DATADOG_STATUS } from '@packages/core/logging';

const DEFAULT_DATADOG_SITE = 'datadoghq.com';
const SERVICE_NAME = 'sts-client';

// Datadog envelope fields; colliding metadata (e.g. an http `status`) would
// corrupt the entry itself, so collisions are re-keyed with a `meta_` prefix.
const RESERVED_ENTRY_KEYS = ['date', 'ddtags', 'message', 'service', 'status'] as const;

// Already carried by the `env` tag, so not repeated as attributes. A `host`
// attribute would also be taken as the log's Host.
const ENV_CONTEXT_KEYS = ['environment', 'host'] as const;

// Same env tags as the backends, so `env:dev` shows client and backend together to correlate.
const DATADOG_ENV: Record<string, string> = { development: 'dev', production: 'prod' };

/** Deployment facts stamped onto every shipped entry. */
export interface DatadogDeploymentInfo {
    /** Deployment environment: development | stage | production. */
    env: string;
    /** Datadog `version` tag: release tag or short commit SHA. */
    version: string;
}

/**
 * Ships entries to Datadog via `@datadog/browser-logs` (owns batching, retry,
 * flush). Created only when a client token is configured, so local development
 * ships nothing by construction.
 */
export class DatadogLogsDestination implements LogDestination {
    readonly name = 'datadog';
    readonly receivesReplay = true;

    /**
     * Floor for shipped entries, applied on top of logger thresholds so loggers
     * that override the global level (e.g. `http` runs at Debug) cannot ship
     * below it; lower levels still reach the console.
     */
    private constructor(readonly minLevel: LogLevel) {}

    /** Initialize the SDK and create the destination; null when no token is configured. */
    static createFromConfig(
        configService: Pick<ConfigService, 'get'>,
        deployment: DatadogDeploymentInfo,
        minLevel: LogLevel
    ): DatadogLogsDestination | null {
        const clientToken = configService.get(Constants.Env.DatadogClientToken, '')?.trim();

        if (!clientToken) {
            return null;
        }

        datadogLogs.init({
            clientToken,
            env: DATADOG_ENV[deployment.env] ?? deployment.env,
            forwardErrorsToLogs: false,
            service: SERVICE_NAME,
            sessionSampleRate: 100,
            site: configService.get(Constants.Env.DatadogSite, DEFAULT_DATADOG_SITE)?.trim() || DEFAULT_DATADOG_SITE,
            version: deployment.version,
        });

        return new DatadogLogsDestination(minLevel);
    }

    /** Ship one entry: context + meta become attributes; Error meta feeds the SDK error param. */
    write(entry: LogEntry): void {
        const { error, ...meta } = entry.meta;
        const errorValue = error instanceof Error ? error : undefined;
        const attributes: Record<string, unknown> = { ...entry.context, ...meta };

        for (const key of ENV_CONTEXT_KEYS) {
            delete attributes[key];
        }

        const context = {
            ...shieldReservedKeys(attributes),
            ...(error !== undefined && errorValue === undefined ? { error } : {}),
            ...(entry.name === '' ? {} : { logger: { name: entry.name } }),
            level: entry.level,
        };

        datadogLogs.logger.log(entry.message, context, LOG_LEVEL_DATADOG_STATUS[entry.level], errorValue);
    }
}

/** Re-key attributes colliding with Datadog's reserved envelope fields. */
function shieldReservedKeys(attributes: Record<string, unknown>): Record<string, unknown> {
    const shielded = { ...attributes };

    for (const key of RESERVED_ENTRY_KEYS) {
        if (key in shielded) {
            shielded[`meta_${key}`] = shielded[key];
            delete shielded[key];
        }
    }

    return shielded;
}
